import autoTable from "jspdf-autotable";
import { createTurkishPdf, applyTurkishFont } from "./pdfFont";

const COMPANY = "LC SOĞUTMA";
const SUBTITLE = "Bayi Operasyon Paneli";
const FONT = "Roboto"; // overridden to "helvetica" if Roboto failed to load

const pickFont = (doc) => {
    // jsPDF font list contains "Roboto" only if applyTurkishFont succeeded.
    const list = doc.getFontList ? doc.getFontList() : {};
    return list && list.Roboto ? "Roboto" : "helvetica";
};

const drawHeader = (doc, title, subtitle) => {
    const font = pickFont(doc);
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 28, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont(font, "bold");
    doc.setFontSize(16);
    doc.text(COMPANY, 14, 12);
    doc.setFont(font, "normal");
    doc.setFontSize(9);
    doc.text(SUBTITLE, 14, 19);

    doc.setFontSize(8);
    doc.text(new Date().toLocaleString("tr-TR"), doc.internal.pageSize.getWidth() - 14, 12, { align: "right" });

    doc.setTextColor(10, 10, 10);
    doc.setFont(font, "bold");
    doc.setFontSize(20);
    doc.text(title, 14, 44);

    if (subtitle) {
        doc.setFont(font, "normal");
        doc.setFontSize(10);
        doc.setTextColor(82, 82, 91);
        doc.text(subtitle, 14, 52);
        doc.setTextColor(10, 10, 10);
    }

    doc.setDrawColor(10, 10, 10);
    doc.setLineWidth(0.4);
    doc.line(14, 58, doc.internal.pageSize.getWidth() - 14, 58);
};

const drawFooter = (doc) => {
    const font = pickFont(doc);
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont(font, "normal");
        doc.setFontSize(8);
        doc.setTextColor(82, 82, 91);
        doc.text(
            `${COMPANY} • Sayfa ${i} / ${pageCount}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 8,
            { align: "center" }
        );
    }
};

const tableStylesPlain = (font) => ({
    font,
    fontSize: 10,
    cellPadding: 3,
});
const tableStylesGrid = (font) => ({
    font,
    fontSize: 9,
    cellPadding: 2.5,
    lineColor: [212, 212, 216],
    lineWidth: 0.1,
});

export const buildOrderPDF = async (order) => {
    const doc = await createTurkishPdf();
    const font = pickFont(doc);
    drawHeader(doc, "SİPARİŞ FORMU", `Sipariş No: ${order.order_no}`);

    autoTable(doc, {
        startY: 64,
        theme: "plain",
        styles: tableStylesPlain(font),
        head: [["ALAN", "DEĞER"]],
        headStyles: { font, fontStyle: "bold", fillColor: [244, 244, 245], textColor: [10, 10, 10], lineWidth: 0.2, lineColor: [212, 212, 216] },
        bodyStyles: { lineWidth: 0.1, lineColor: [228, 228, 231] },
        body: [
            ["Sipariş No", order.order_no],
            ["Durum", order.status === "cancelled" ? "İptal" : (order.status === "revised" ? "Revize" : "Aktif")],
            ["Tarih", new Date(order.created_at).toLocaleString("tr-TR")],
            ["Bayi", `${order.bayi_name} (${order.bayi_username})`],
            ["Klima Modeli", order.klima_modeli],
            ["İlave Akü", order.ilave_aku],
            ["Adet", String(order.adet)],
            ["Notlar", order.notlar || "-"],
            ...(order.cancel_reason ? [["İptal Sebebi", order.cancel_reason]] : []),
            ...(order.last_revision_note ? [["Son Revizyon", order.last_revision_note]] : []),
        ],
    });

    drawFooter(doc);
    return { blob: doc.output("blob"), filename: `siparis-${order.order_no}.pdf` };
};

export const buildOrdersListPDF = async (orders) => {
    const doc = await createTurkishPdf({ orientation: "landscape" });
    const font = pickFont(doc);
    drawHeader(doc, "SİPARİŞ LİSTESİ", `Toplam ${orders.length} kayıt`);

    autoTable(doc, {
        startY: 64,
        theme: "grid",
        styles: tableStylesGrid(font),
        headStyles: { font, fontStyle: "bold", fillColor: [10, 10, 10], textColor: [255, 255, 255] },
        head: [["Sipariş No", "Durum", "Tarih", "Bayi", "Klima", "İlave Akü", "Adet", "Notlar"]],
        body: orders.map((o) => [
            o.order_no,
            o.status === "cancelled" ? "İptal" : (o.status === "revised" ? "Revize" : "Aktif"),
            new Date(o.created_at).toLocaleString("tr-TR"),
            o.bayi_name,
            o.klima_modeli,
            o.ilave_aku,
            o.adet,
            o.notlar || "-",
        ]),
    });

    drawFooter(doc);
    return { blob: doc.output("blob"), filename: `siparis-listesi-${Date.now()}.pdf` };
};

export const buildWarrantyPDF = async (w, imageDataUrl = null) => {
    const doc = await createTurkishPdf();
    const font = pickFont(doc);
    drawHeader(doc, "GARANTİ BELGESİ", `Garanti No: ${w.warranty_no}`);

    autoTable(doc, {
        startY: 64,
        theme: "plain",
        styles: tableStylesPlain(font),
        head: [["ALAN", "DEĞER"]],
        headStyles: { font, fontStyle: "bold", fillColor: [244, 244, 245], textColor: [10, 10, 10], lineWidth: 0.2, lineColor: [212, 212, 216] },
        bodyStyles: { lineWidth: 0.1, lineColor: [228, 228, 231] },
        body: [
            ["Garanti No", w.warranty_no],
            ["Tarih", new Date(w.created_at).toLocaleString("tr-TR")],
            ["Montaj Tarihi", w.montaj_tarihi ? new Date(w.montaj_tarihi).toLocaleDateString("tr-TR") : new Date(w.created_at).toLocaleDateString("tr-TR")],
            ["Bayi", `${w.bayi_name} (${w.bayi_username})`],
            ["Klima Modeli", w.klima_modeli],
            ["İlave Akü", w.ilave_aku],
            ["Akü Bilgisi", w.aku_bilgisi || "-"],
            ["Araç Bilgisi", w.arac_bilgisi || "-"],
            ["Seri No", w.seri_no || "-"],
        ],
    });

    if (imageDataUrl) {
        const finalY = doc.lastAutoTable.finalY + 10;
        const pageHeight = doc.internal.pageSize.getHeight();
        const yPos = finalY > pageHeight - 80 ? (doc.addPage(), applyTurkishFont(doc), 20) : finalY;
        doc.setFont(font, "bold");
        doc.setFontSize(11);
        doc.text("ARAÇ FOTOĞRAFI", 14, yPos);
        try {
            doc.addImage(imageDataUrl, "JPEG", 14, yPos + 4, 100, 70);
        } catch (e) {
            try { doc.addImage(imageDataUrl, "PNG", 14, yPos + 4, 100, 70); } catch (_) {}
        }
    }

    drawFooter(doc);
    return { blob: doc.output("blob"), filename: `garanti-${w.warranty_no}.pdf` };
};

export const buildWarrantiesListPDF = async (warranties) => {
    const doc = await createTurkishPdf({ orientation: "landscape" });
    const font = pickFont(doc);
    drawHeader(doc, "GARANTİ LİSTESİ", `Toplam ${warranties.length} kayıt`);

    autoTable(doc, {
        startY: 64,
        theme: "grid",
        styles: tableStylesGrid(font),
        headStyles: { font, fontStyle: "bold", fillColor: [10, 10, 10], textColor: [255, 255, 255] },
        head: [["Garanti No", "Sipariş No", "Tarih", "Montaj Tarihi", "Bayi", "Klima", "İlave Akü", "Seri No"]],
        body: warranties.map((w) => [
            w.warranty_no,
            w.order_no || "-",
            new Date(w.created_at).toLocaleString("tr-TR"),
            w.montaj_tarihi ? new Date(w.montaj_tarihi).toLocaleDateString("tr-TR") : new Date(w.created_at).toLocaleDateString("tr-TR"),
            w.bayi_name,
            w.klima_modeli,
            w.ilave_aku,
            w.seri_no || "-",
        ]),
    });

    drawFooter(doc);
    return { blob: doc.output("blob"), filename: `garanti-listesi-${Date.now()}.pdf` };
};
